import React, { useState, useEffect } from 'react';
import { Search, RefreshCw, AlertCircle, Building2, MessageSquare, Calendar, Edit2, RotateCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';

interface Empresa {
  id: string;
  nombre: string;
  codigo_fiscal: string;
  mensajes_max: number;
  tipo_suscripcion: string;
  estado: string;
  created_at: string;
}

interface MensajesContador {
  id: string;
  empresa_id: string;
  mes_inicio: string;
  mes_fin: string;
  mensajes_enviados: number;
  created_at: string;
  updated_at: string;
  empresa?: {
    nombre: string;
    codigo_fiscal: string;
    mensajes_max: number;
  };
}

export default function EmpresaMensajesLimites() {
  const { user } = useAuthStore();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [contadores, setContadores] = useState<MensajesContador[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmpresa, setSelectedEmpresa] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState<Empresa | null>(null);
  const [newMensajesMax, setNewMensajesMax] = useState<number>(0);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmpresaId, setResetEmpresaId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchEmpresas();
    }
  }, [user]);

  useEffect(() => {
    if (selectedEmpresa) {
      fetchContadores(selectedEmpresa);
    } else {
      fetchAllContadores();
    }
  }, [selectedEmpresa]);

  const fetchEmpresas = async () => {
    try {
      setIsLoading(true);
      setError('');

      const { data, error } = await supabase
        .from('empresas')
        .select('*')
        .order('nombre');

      if (error) throw error;
      setEmpresas(data || []);
    } catch (error: any) {
      console.error('Error fetching empresas:', error);
      setError('Error al cargar las empresas');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchContadores = async (empresaId: string) => {
    try {
      setIsLoading(true);
      setError('');

      const { data, error } = await supabase
        .from('mensajes_empresa_contador')
        .select(`
          *,
          empresa:empresas(nombre, codigo_fiscal, mensajes_max)
        `)
        .eq('empresa_id', empresaId)
        .order('mes_inicio', { ascending: false });

      if (error) throw error;
      setContadores(data || []);
    } catch (error: any) {
      console.error('Error fetching contadores:', error);
      setError('Error al cargar los contadores de mensajes');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllContadores = async () => {
    try {
      setIsLoading(true);
      setError('');

      const { data, error } = await supabase
        .from('mensajes_empresa_contador')
        .select(`
          *,
          empresa:empresas(nombre, codigo_fiscal, mensajes_max)
        `)
        .order('mes_inicio', { ascending: false });

      if (error) throw error;
      setContadores(data || []);
    } catch (error: any) {
      console.error('Error fetching all contadores:', error);
      setError('Error al cargar los contadores de mensajes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateMensajesMax = async () => {
    if (!editingEmpresa) return;

    try {
      setError('');
      setSuccess('');

      const { error } = await supabase
        .from('empresas')
        .update({ mensajes_max: newMensajesMax })
        .eq('id', editingEmpresa.id);

      if (error) throw error;

      setSuccess(`Límite de mensajes actualizado correctamente para ${editingEmpresa.nombre}`);
      setShowModal(false);
      setEditingEmpresa(null);
      
      // Refresh data
      fetchEmpresas();
      if (selectedEmpresa) {
        fetchContadores(selectedEmpresa);
      } else {
        fetchAllContadores();
      }
    } catch (error: any) {
      console.error('Error updating mensajes_max:', error);
      setError(error.message);
    }
  };

  const handleEditEmpresa = (empresa: Empresa) => {
    setEditingEmpresa(empresa);
    setNewMensajesMax(empresa.mensajes_max);
    setShowModal(true);
  };

  const handleResetCounter = async () => {
    if (!resetEmpresaId) return;

    try {
      setIsResetting(true);
      setError('');
      setSuccess('');

      // Get the current counter
      const { data: currentCounter, error: fetchError } = await supabase
        .from('mensajes_empresa_contador')
        .select('*')
        .eq('empresa_id', resetEmpresaId)
        .order('mes_inicio', { ascending: false })
        .limit(1)
        .single();

      if (fetchError) throw fetchError;
      if (!currentCounter) throw new Error('No se encontró el contador actual');

      // Calculate new period dates
      const mesFinDate = new Date(currentCounter.mes_fin);
      const newMesInicio = new Date();
      
      // Set to today
      const newMesFin = new Date(newMesInicio);
      newMesFin.setMonth(newMesFin.getMonth() + 1);
      newMesFin.setDate(newMesFin.getDate() - 1);

      const newMesInicioStr = newMesInicio.toISOString().split('T')[0];
      const newMesFinStr = newMesFin.toISOString().split('T')[0];

      // Create new counter record with 0 messages
      const { error: insertError } = await supabase
        .from('mensajes_empresa_contador')
        .upsert({
          empresa_id: resetEmpresaId,
          mes_inicio: newMesInicioStr,
          mes_fin: newMesFinStr,
          mensajes_enviados: 0
        }, {
          onConflict: 'empresa_id,mes_inicio,mes_fin'
        });

      if (insertError) throw insertError;

      setSuccess('Contador de mensajes reiniciado correctamente');
      setShowResetModal(false);
      setResetEmpresaId(null);
      
      // Refresh data
      if (selectedEmpresa) {
        fetchContadores(selectedEmpresa);
      } else {
        fetchAllContadores();
      }
    } catch (error: any) {
      console.error('Error resetting message counter:', error);
      setError(error.message);
    } finally {
      setIsResetting(false);
    }
  };

  const handleShowResetModal = (empresaId: string) => {
    setResetEmpresaId(empresaId);
    setShowResetModal(true);
  };

  const filteredContadores = contadores.filter(contador => {
    if (searchTerm) {
      return (
        contador.empresa?.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contador.empresa?.codigo_fiscal.toLowerCase().includes(searchTerm.toLowerCase()) ||
        new Date(contador.mes_inicio).toLocaleDateString().includes(searchTerm) ||
        new Date(contador.mes_fin).toLocaleDateString().includes(searchTerm)
      );
    }
    return true;
  });

  const calculatePercentage = (enviados: number, max: number | undefined): number => {
    if (!max || max === 0) return 0;
    return Math.min(Math.round((enviados / max) * 100), 100);
  };

  const getProgressColor = (percentage: number): string => {
    if (percentage < 70) return 'bg-green-500';
    if (percentage < 90) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const isCurrentPeriod = (mesFin: string): boolean => {
    const today = new Date();
    const finDate = new Date(mesFin);
    
    // Check if the end date is in the future or today
    return finDate >= today;
  };

  if (isLoading && contadores.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Control de Límites de Mensajes</h1>
        <button
          onClick={() => {
            if (selectedEmpresa) {
              fetchContadores(selectedEmpresa);
            } else {
              fetchAllContadores();
            }
          }}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
          title="Actualizar datos"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[300px]">
            <input
              type="text"
              placeholder="Buscar por empresa, CIF o fecha..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>

          <select
            value={selectedEmpresa}
            onChange={(e) => setSelectedEmpresa(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md min-w-[200px]"
          >
            <option value="">Todas las empresas</option>
            {empresas.map(empresa => (
              <option key={empresa.id} value={empresa.id}>
                {empresa.nombre} ({empresa.codigo_fiscal})
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left border-b">
                <th className="pb-3 font-semibold text-gray-600">Empresa</th>
                <th className="pb-3 font-semibold text-gray-600">Período</th>
                <th className="pb-3 font-semibold text-gray-600">Mensajes</th>
                <th className="pb-3 font-semibold text-gray-600">Progreso</th>
                <th className="pb-3 font-semibold text-gray-600">Última Actualización</th>
                <th className="pb-3 font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredContadores.map((contador) => {
                const percentage = calculatePercentage(contador.mensajes_enviados, contador.empresa?.mensajes_max);
                const progressColor = getProgressColor(percentage);
                const isPeriodCurrent = isCurrentPeriod(contador.mes_fin);
                
                return (
                  <tr key={contador.id} className={`border-b ${isPeriodCurrent ? 'bg-blue-50' : ''}`}>
                    <td className="py-3">
                      <div className="flex items-center">
                        <Building2 className="w-5 h-5 text-blue-500 mr-2" />
                        <div>
                          <div className="font-medium">{contador.empresa?.nombre}</div>
                          <div className="text-sm text-gray-500">{contador.empresa?.codigo_fiscal}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-500 mr-2" />
                        <div>
                          <div className="text-sm">
                            {new Date(contador.mes_inicio).toLocaleDateString()} - {new Date(contador.mes_fin).toLocaleDateString()}
                          </div>
                          {isPeriodCurrent && (
                            <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full mt-1">
                              Período actual
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center">
                        <MessageSquare className="w-4 h-4 text-gray-500 mr-2" />
                        <div>
                          <div className="font-medium">
                            {contador.mensajes_enviados} / {contador.empresa?.mensajes_max === 0 ? 'Ilimitado' : contador.empresa?.mensajes_max}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className={`${progressColor} h-2.5 rounded-full`} 
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 text-right">
                        {percentage}%
                      </div>
                    </td>
                    <td className="py-3 text-sm text-gray-500">
                      {new Date(contador.updated_at).toLocaleString()}
                    </td>
                    <td className="py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            const empresa = empresas.find(e => e.id === contador.empresa_id);
                            if (empresa) {
                              handleEditEmpresa(empresa);
                            }
                          }}
                          className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                          title="Editar límite de mensajes"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        {isPeriodCurrent && (
                          <button
                            onClick={() => handleShowResetModal(contador.empresa_id)}
                            className="p-1 text-orange-500 hover:bg-orange-100 rounded-full"
                            title="Reiniciar contador"
                          >
                            <RotateCw className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredContadores.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No se encontraron registros de mensajes</p>
                    {searchTerm && <p className="text-sm mt-2">Prueba con otros términos de búsqueda</p>}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para editar límite de mensajes */}
      {showModal && editingEmpresa && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              Editar Límite de Mensajes
            </h2>
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-1">
                Empresa
              </div>
              <div className="flex items-center">
                <Building2 className="w-5 h-5 text-blue-500 mr-2" />
                <div>
                  <div className="font-medium">{editingEmpresa.nombre}</div>
                  <div className="text-sm text-gray-500">{editingEmpresa.codigo_fiscal}</div>
                </div>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Límite de Mensajes Mensuales
              </label>
              <input
                type="number"
                min="0"
                value={newMensajesMax}
                onChange={(e) => setNewMensajesMax(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              <p className="text-xs text-gray-500 mt-1">
                Establece 0 para mensajes ilimitados
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingEmpresa(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleUpdateMensajesMax}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Actualizar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para reiniciar contador */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              Reiniciar Contador de Mensajes
            </h2>
            <div className="mb-6">
              <p className="text-gray-700">
                ¿Estás seguro de que deseas reiniciar el contador de mensajes para esta empresa? 
                Esta acción creará un nuevo período que comenzará hoy y reiniciará el conteo a cero.
              </p>
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-700 text-sm">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Esta acción no se puede deshacer y afectará inmediatamente a la facturación.
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowResetModal(false);
                  setResetEmpresaId(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleResetCounter}
                disabled={isResetting}
                className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 flex items-center"
              >
                {isResetting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Reiniciando...
                  </>
                ) : (
                  <>
                    <RotateCw className="w-4 h-4 mr-2" />
                    Reiniciar Contador
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}